import { getApiClientRuntime } from '../config';
import { networkError, toApiError, type ApiError } from './apiError';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface ApiRequestArgs {
  /** Path under the configured base url, leading slash included: `/auth/me`. */
  readonly url: string;
  readonly method?: HttpMethod;
  readonly body?: unknown;
  readonly params?: QueryParams;
}

/** `meta` of the success envelope; carried through to `transformResponse`. */
export interface EnvelopeMeta {
  readonly requestId: string;
  readonly serverTime: string;
  readonly page?: number;
  readonly pageSize?: number;
  readonly total?: number;
  readonly totalPages?: number;
}

export interface HttpSuccess {
  readonly data: unknown;
  readonly meta: EnvelopeMeta;
}

/**
 * The refresh cookie is `Path=/api/v1/auth` and `httpOnly`, so it is only ever
 * relevant on the auth routes. Sending credentials elsewhere would be a
 * no-op at best and a CORS pre-flight failure at worst.
 */
export function isAuthPath(url: string): boolean {
  return url.startsWith('/auth/') || url === '/auth';
}

function buildUrl(baseUrl: string, url: string, params: QueryParams | undefined): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null) continue;
    search.set(key, String(value));
  }
  // Amendment #20: an explicit `?lang=` wins over every other locale source,
  // which is exactly what the UI wants — the server message must match the
  // language the screen is currently rendered in.
  search.set('lang', getApiClientRuntime().getLocale());
  return `${baseUrl}${url}?${search.toString()}`;
}

function isEnvelope(value: unknown): value is { data: unknown; meta: EnvelopeMeta } {
  return typeof value === 'object' && value !== null && 'data' in value && 'meta' in value;
}

/**
 * One request. Unwraps `{ data, meta }`, feeds `meta.serverTime` to the clock
 * anchor, and rejects with an {@link ApiError} — never with a bare `Response`.
 */
export async function apiFetch(
  args: ApiRequestArgs,
  options: { token?: string | null; signal?: AbortSignal } = {},
): Promise<HttpSuccess> {
  const runtime = getApiClientRuntime();
  const method = args.method ?? 'GET';
  const headers: Record<string, string> = {
    accept: 'application/json',
    'accept-language': runtime.getLocale(),
  };
  if (args.body !== undefined) headers['content-type'] = 'application/json';
  if (options.token) headers['authorization'] = `Bearer ${options.token}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(runtime.baseUrl, args.url, args.params), {
      method,
      headers,
      ...(args.body === undefined ? {} : { body: JSON.stringify(args.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
      // Only the auth routes carry the refresh cookie (D-05).
      credentials: isAuthPath(args.url) ? 'include' : 'same-origin',
    });
  } catch (cause) {
    throw networkError(cause);
  }

  const raw: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw toApiError(response.status, raw, response.headers.get('x-request-id') ?? 'unknown');
  }
  if (!isEnvelope(raw)) {
    throw toApiError(response.status, raw, response.headers.get('x-request-id') ?? 'unknown');
  }

  runtime.onServerTime(raw.meta.serverTime);
  return { data: raw.data, meta: raw.meta };
}

export type { ApiError };
